import { Add, AddComment, Locked as LockedIcon, Unlocked as UnlockIcon, View as ViewIcon } from '@carbon/icons-react';
import { ActionsOverlay, ActionsOverlayAction } from 'component/actions-overlay';
import { Banner } from 'component/banner';
import { FlowBox, HorizBox, HoverView, Pad, PadBox, Separator, VertBox } from 'component/basics';
import { type ButtonType, CTAButton, FilterButton, Tag, TextButton } from 'component/button';
import {
    colorBorderPrimary,
    colorBorderSelected,
    colorPrimaryBrandIris,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTagHighlight,
    colorTagSuccess,
    colorTextOnPrimaryButton,
    colorTextPrimary,
    colorTextSecondary,
    colorTextWarning,
} from 'component/color';
import { fromNow } from 'component/date';
import { FormField, SearchField } from 'component/form';
import { IconSparkleStars } from 'component/icon';
import { useGlobalQuestions } from 'component/question';
import { ConversationScreen } from 'component/screen';
import { CharacterCounter, checkValidLength, Heading, TextField, UtilityText } from 'component/text';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import type { EditorialDashConfig } from 'structure/editorial-dash';
import type { QuestionConfig } from 'structure/question';
import { QuestionActionsOverlay } from 'structure/question/actions-overlay';
import { QUESTION_MAX_NAME_LENGTH } from 'structure/question/constants';
import { useQuestionNameValidation } from 'structure/question/form';
import { useGlobalTopics } from 'structure/topic';
import { Catcher, CatchList } from 'system/catcher';
import { useHasCapability } from 'util/admin';
import { Datastore, useDatastore, useGlobalProperty, useSessionData } from 'util/datastore';
import { useConfig } from 'util/features';
import type { Feature, TLabel } from 'util/stdtypes';
import { labelToKey, nullS, t } from 'util/stdtypes';
import { makeAssetUrl } from 'util/util';
export type Space = {
    key: string;
    name: string;
    structureKey: string;
    commentCount?: number;
    format?: string;
    status?: string;
    time?: number;
};

export type SpaceFilter = {
    key: string;
    widget: React.ComponentType;
    filter: (datastore: Datastore, space: Space) => boolean;
};

export type SpaceWidgetProps = {
    space: Space;
};
export type SpaceColumn = {
    label: TLabel;
    alignRight?: boolean;
    widget: React.ComponentType<SpaceWidgetProps>;
};

export const ResponsesColumn: SpaceColumn = {
    label: t('Responses'),
    widget: ({ space }) => <UtilityText text={space.commentCount?.toString() ?? '0'} type='small' />,
};

function FormatWidget({ space }: SpaceWidgetProps) {
    const { templates } = useConfig<SpacesConfig>();
    const template = templates.find((template) => template.key === space.format);
    if (!template) return null;
    return <Tag color={colorSurfaceSecondary} label={template?.label} overrideTinyTextColor type='tiny' />;
}

export const FormatColumn: SpaceColumn = {
    label: t('Format'),
    widget: FormatWidget,
};

export const CreatedColumn: SpaceColumn = {
    label: t('Created'),
    widget: ({ space }) => <UtilityText text={fromNow(space.time) ?? 'N/A'} type='small' />,
};

export type StatusWidgetProps = SpaceWidgetProps;

export function StatusWidget({ space }: StatusWidgetProps) {
    const status = space.status ?? 'active';
    switch (status) {
        case 'active':
            return <Tag color={colorTagSuccess} emoji='🚀' label={t('Active')} overrideTinyTextColor type='tiny' />;
        case 'closed':
            return <Tag color={colorTagHighlight} emoji='🔒' label={t('Closed')} overrideTinyTextColor type='tiny' />;
        default:
            return <Tag color={colorSurfaceSecondary} label={t('N/A')} overrideTinyTextColor type='tiny' />;
    }
}

export const StatusColumn: SpaceColumn = {
    label: t('Status'),
    widget: StatusWidget,
};

export type SpaceActionsOverlayProps = SpaceWidgetProps;

export function SpaceActionsOverlay({ space }: SpaceActionsOverlayProps) {
    const datastore = useDatastore();

    if (space.structureKey !== 'question') {
        return null;
    }

    return (
        <Datastore
            appData={datastore.props.appData}
            config={datastore.getConfig()}
            globalParams={datastore.props.globalParams}
            instanceKey={space.key}
            isEmbedded={true}
            isLive={datastore.props.isLive}
            language={datastore.props.language}
            personaPreview={datastore.props.personaPreview}
            readOnly={datastore.props.readOnly}
            siloKey={datastore.props.siloKey}
            structureKey={space.structureKey}
            testState={datastore.props.testState}
        >
            <QuestionActionsOverlay />
        </Datastore>
    );
}

export const ActionsColumn: SpaceColumn = {
    label: t('Actions'),
    alignRight: true,
    widget: SpaceActionsOverlay,
};

export const ConversationFormatPreview = React.forwardRef<
    View,
    {
        template: SpaceTemplate;
        selected: boolean;
        onPress: () => void;
    }
>(({ template, selected, onPress }, ref) => {
    const s = ConversationFormatPreviewStyle;
    return (
        <HoverView onPress={onPress} style={s.container}>
            <View ref={ref} style={selected ? s.selected : s.default}>
                <Image source={{ uri: makeAssetUrl(template.imageUrl) }} style={s.image} />
            </View>
            <Pad size={8} />
            <UtilityText label={template.label} type='small' weight='medium' />
        </HoverView>
    );
});

ConversationFormatPreview.displayName = 'ConversationFormatPreview';
const ConversationFormatPreviewStyle = StyleSheet.create({
    container: {},
    image: {
        width: 187,
        height: 130,
    },
    selected: {
        borderColor: colorBorderSelected,
        borderWidth: 1,
        borderRadius: 4,
    },
    default: {
        borderColor: colorSurfaceSecondary,
        borderWidth: 1,
        borderRadius: 4,
    },
});

export type ConversationFormatHelpProps = { template: SpaceTemplate; topOffset?: number };

export function ConversationFormatHelp({ template, topOffset }: ConversationFormatHelpProps) {
    const s = ConversationFormatHelpStyle;
    return (
        <View style={[s.container, topOffset !== undefined && { top: topOffset }]}>
            <HorizBox center gap={8}>
                <IconSparkleStars color={colorPrimaryBrandIris} />
                <UtilityText color={colorPrimaryBrandIris} label={template.label} type='large' weight='medium' />
            </HorizBox>
            <Pad size={20} />
            <UtilityText color={colorTextSecondary} label={template.description} type='small' />
        </View>
    );
}

const ConversationFormatHelpStyle = StyleSheet.create({
    container: {
        backgroundColor: colorSurfacePrimary,
        borderRadius: 16,
        padding: 20,
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
        width: 312,
        position: 'absolute',
        right: 0,
        zIndex: 1,
        marginRight: -336,
    },
});

export type CoversationFormatEditorProps = {
    spaceProps: Record<string, any>;
    setSpaceProps: (props: Record<string, any>) => void;
    selectedTemplateKey: string | null;
    setSelectedTemplateKey: (key: string) => void;
};

export function CoversationFormatEditor({
    spaceProps,
    setSpaceProps,
    selectedTemplateKey,
    setSelectedTemplateKey,
}: CoversationFormatEditorProps) {
    const { templates } = useConfig<SpacesConfig>();
    const selectedTemplate = templates.find((template) => template.key === selectedTemplateKey);
    const [selectedTemplateTop, setSelectedTemplateTop] = useState<number | undefined>(undefined);
    const templateContainerRef = useRef<View>(null);
    const selectedTemplateRef = useRef<View>(null);

    const updateHelpPosition = useCallback(() => {
        if (templateContainerRef.current && selectedTemplateRef.current) {
            templateContainerRef.current.measure((_a, _b, _c, _d, _e, templateContainerY) => {
                selectedTemplateRef.current?.measure((_f, _g, _h, _i, _j, templatePageY) => {
                    const offset = templatePageY - templateContainerY;
                    setSelectedTemplateTop(offset);
                });
            });
        }
    }, []);

    useEffect(() => {
        if (selectedTemplate) {
            // Small delay to ensure the refs are properly attached
            setTimeout(updateHelpPosition, 0);
        }
    }, [selectedTemplate, updateHelpPosition]);

    return (
        <View>
            <UtilityText label={t('Prosocial format (required)')} type='small' weight='medium' />
            <Pad size={8} />
            <View ref={templateContainerRef}>
                <FlowBox columnGap={16} rowGap={16}>
                    {selectedTemplate && <ConversationFormatHelp template={selectedTemplate} topOffset={selectedTemplateTop} />}
                    {templates.map((template) => (
                        <ConversationFormatPreview
                            key={template.key}
                            onPress={() => {
                                setSelectedTemplateKey(template.key);
                                setSpaceProps({});
                            }}
                            ref={selectedTemplateKey === template.key ? selectedTemplateRef : undefined}
                            selected={selectedTemplateKey === template.key}
                            template={template}
                        />
                    ))}
                </FlowBox>
            </View>
            {selectedTemplate?.configWidget && (
                <>
                    <Pad size={32} />
                    <selectedTemplate.configWidget setSpaceProps={setSpaceProps} spaceProps={spaceProps} />
                </>
            )}
        </View>
    );
}

async function createSpaceAsync(
    datastore: Datastore,
    template: SpaceTemplate,
    name: string,
    spaceProps: SpaceProps,
    onCreateCallbacks: ((datastore: Datastore, questionKey: string, values: Record<string, any>) => void)[],
    createCallbackValues: Record<string, any>,
) {
    let features = {};
    if (template?.featuresEnabled) {
        features = template.featuresEnabled.reduce((acc, feature) => {
            acc[feature] = true;
            return acc;
        }, {});
    }
    if (template?.featuresDisabled) {
        features = template.featuresDisabled.reduce((acc, feature) => {
            acc[feature] = false;
            return acc;
        }, features);
    }
    const response = await datastore.callServerAsync('questions', 'add', {
        newInstance: { name, features },
        globals: spaceProps,
        format: template.key,
    });

    if (response?.questionKey) {
        if (onCreateCallbacks) {
            for (const callback of onCreateCallbacks) {
                callback(datastore, response.questionKey, createCallbackValues);
            }
        }
        datastore.gotoInstanceScreen({
            structureKey: 'question',
            instanceKey: response.questionKey,
            screenKey: 'edit',
            replace: true,
        });
    } else {
        datastore.goBack();
    }
}

type NewSpaceScreenWidgetsProps = {
    slot: 'after-name';
    values: Record<string, any>;
    setValues: (values: Record<string, any>) => void;
};

function NewSpaceScreenWidgets({ slot, values, setValues }: NewSpaceScreenWidgetsProps) {
    const { newScreenWidgets } = useConfig<SpacesConfig>();
    const widgetsForSection = newScreenWidgets?.filter((widget) => widget.slot === slot) ?? [];
    if (widgetsForSection.length === 0) {
        return null;
    }
    return (
        <>
            {widgetsForSection.map((widget, i) => (
                <Catcher key={i}>
                    <widget.component setValues={setValues} values={values} />
                </Catcher>
            ))}
        </>
    );
}

export function NewSpaceScreen() {
    const { templates, newScreenWidgets } = useConfig<SpacesConfig>();
    const { dashboardCapability } = useConfig<EditorialDashConfig>();
    const [selectedTemplateKey, setSelectedTemplateKey] = useState(nullS);
    const [spaceProps, setSpaceProps] = useState({});
    const [widgetValues, setWidgetValues] = useState({});
    const [name, setName] = useState(nullS);
    const [nameError, setNameError] = useState<JSX.Element | undefined>(undefined);
    const datastore = useDatastore();
    const selectedTemplate = templates.find((template) => template.key === selectedTemplateKey);
    const createDisabled =
        !selectedTemplate ||
        !name ||
        !!nameError ||
        (selectedTemplate && selectedTemplate.isCreateAllowed && !selectedTemplate.isCreateAllowed({ spaceProps }));
    const hasCapability = useHasCapability(dashboardCapability);
    const handleQuestionNameValidation = useQuestionNameValidation(setNameError);

    if (!hasCapability) {
        return (
            <Banner>
                <UtilityText label={t('You do not have access to this feature')} />
            </Banner>
        );
    }
    if (templates.length === 0) {
        return <NoTemplatesAvailableMessage />;
    }
    return (
        <ConversationScreen noWrap>
            <Pad size={32} />
            <VertBox gap={32} wide>
                <Heading label={t('New space')} type='large' weight='medium' />
                <FormField
                    accessoryAction={<CharacterCounter max={QUESTION_MAX_NAME_LENGTH} text={name ?? ''} />}
                    label={t('Prompt')}
                    required
                >
                    <TextField
                        error={!!nameError}
                        multiline={false}
                        onChange={(value) => {
                            setName(value);
                            handleQuestionNameValidation(value);
                        }}
                        placeholderLabel={t('Write a question or discussion prompt...')}
                        value={name}
                    />
                    {nameError && <PadBox top={8}>{nameError}</PadBox>}
                </FormField>
                <NewSpaceScreenWidgets setValues={setWidgetValues} slot='after-name' values={widgetValues} />
                <CoversationFormatEditor
                    selectedTemplateKey={selectedTemplateKey}
                    setSelectedTemplateKey={setSelectedTemplateKey}
                    setSpaceProps={setSpaceProps}
                    spaceProps={spaceProps}
                />
            </VertBox>
            <Pad size={32} />
            <HorizBox right>
                <CTAButton label={t('Cancel')} onPress={() => datastore.goBack()} type='secondary' />
                <Pad size={16} />
                <CTAButton
                    disabled={createDisabled}
                    label={t('Create space')}
                    onPress={() => {
                        if (!selectedTemplate || !name) {
                            return;
                        }
                        const onCreateCallbacks =
                            newScreenWidgets?.map((widget) => widget.onCreate).filter((callback) => callback !== undefined) ?? [];
                        createSpaceAsync(datastore, selectedTemplate, name, spaceProps, onCreateCallbacks, widgetValues);
                    }}
                />
            </HorizBox>
            <Pad size={80} />
        </ConversationScreen>
    );
}

function NoTemplatesAvailableMessage() {
    return (
        <View>
            <Pad size={32} />
            <Heading label={t('No templates available')} type='large' weight='medium' />
            <Pad size={80} />
        </View>
    );
}

export type SpacesTableRowsProps = { spaceList: Space[]; usingSearchOrFilters: boolean };

export function SpacesTableRows({ spaceList, usingSearchOrFilters }: SpacesTableRowsProps) {
    const s = SpacesTableRowsStyle;
    const datastore = useDatastore();
    const { columns } = useConfig<SpacesConfig>();
    return (
        <View>
            <HorizBox spread>
                <View style={s.titleCol}>
                    <UtilityText caps color={colorTextSecondary} label={t('Name')} type='small' />
                </View>
                {columns.map((column) => (
                    <View key={labelToKey(column.label)} style={[s.col]}>
                        <View style={column.alignRight ? s.alignRight : null}>
                            <UtilityText caps color={colorTextSecondary} key={labelToKey(column.label)} label={column.label} type='small' />
                        </View>
                    </View>
                ))}
            </HorizBox>
            <CatchList
                items={spaceList}
                renderItem={(space, isLast) => (
                    <PadBox key={space.key} vert={20}>
                        <HorizBox center>
                            <View style={s.titleCol}>
                                <TextButton
                                    onPress={() => {
                                        space.structureKey === 'question'
                                            ? datastore.gotoInstanceScreen({
                                                  structureKey: space.structureKey,
                                                  instanceKey: space.key,
                                                  screenKey: 'edit',
                                              })
                                            : datastore.gotoInstance({
                                                  structureKey: space.structureKey,
                                                  instanceKey: space.key,
                                              });
                                    }}
                                    strong
                                    text={space.name}
                                    type='small'
                                />
                            </View>
                            {columns.map((column) => (
                                <View key={labelToKey(column.label)} style={s.col}>
                                    <column.widget space={space} />
                                </View>
                            ))}
                        </HorizBox>
                    </PadBox>
                )}
                separator={Separator}
            />
            {spaceList.length === 0 && (
                <>
                    <Pad size={24} />
                    <EmptySpaceTableBanner usingSearchOrFilters={usingSearchOrFilters} />
                </>
            )}
        </View>
    );
}

const SpacesTableRowsStyle = StyleSheet.create({
    alignRight: {
        alignSelf: 'flex-end',
    },
    titleCol: {
        flex: 3,
        flexBasis: 0,
        paddingRight: 16,
    },
    col: {
        flex: 1,
        flexBasis: 0,
        paddingRight: 16,
    },
});

function EmptySpaceTableBanner({ usingSearchOrFilters }: { usingSearchOrFilters: boolean }) {
    const s = EmptySpaceTableBannerStyle;
    return (
        <View style={s.banner}>
            <VertBox center>
                <Image source={{ uri: makeAssetUrl('images/spaces.png') }} style={s.image} />
                <Pad size={12} />
                <UtilityText center label={usingSearchOrFilters ? t('No spaces found') : t('No spaces yet')} type='large' weight='medium' />
            </VertBox>
        </View>
    );
}

const EmptySpaceTableBannerStyle = StyleSheet.create({
    banner: {
        backgroundColor: colorSurfaceSecondary,
        borderRadius: 8,
        padding: 24,
    },
    image: {
        width: 133,
        height: 80,
    },
});

function useSpaceList(): Space[] {
    const questionList = useGlobalQuestions();
    const topicList = useGlobalTopics();

    const questionSpaceList = questionList.map((question) => ({
        ...question,
        key: question.key,
        name: question.question,
        structureKey: 'question',
        status: (question as any).readOnly ? 'closed' : 'active',
    }));

    const topicSpaceList = topicList.map((topic) => ({
        ...topic,
        key: topic.key,
        name: topic.name,
        structureKey: 'topic',
        status: 'active',
    }));

    const combinedSpaceList = [...questionSpaceList, ...topicSpaceList];

    const sortedSpaceList = combinedSpaceList.sort((a, b) => (b.time ?? 0) - (a.time ?? 0));

    return sortedSpaceList;
}

export function SpacesTable() {
    const s = SpacesTableStyle;
    const { filters } = useConfig<SpacesConfig>();
    const datastore = useDatastore();
    const [search, setSearch] = useState('');
    const spaceList = useSpaceList();

    const searchFilteredSpaceList = spaceList.filter((space) => space.name?.toLowerCase().includes(search.toLowerCase()) ?? false);
    const filteredSpaceList = searchFilteredSpaceList.filter((space) => filters.every((filter) => filter.filter(datastore, space)));

    const usingSearchOrFilters = spaceList.length > filteredSpaceList.length;

    return (
        <View style={s.table}>
            <HorizBox center spread>
                <Heading label={t('Spaces')} type='large' weight='medium' />
                <SearchField onChange={setSearch} placeholder='Search...' size='small' value={search} />
            </HorizBox>
            <Pad size={20} />
            <HorizBox center spread>
                <HorizBox>
                    {filters.map((filter) => (
                        <filter.widget key={filter.key} />
                    ))}
                </HorizBox>
                <CTAButton
                    icon={<Add style={{ width: 20, height: 20, fill: colorTextOnPrimaryButton }} />}
                    label={t('New space')}
                    onPress={() => datastore.pushSubscreen('newSpace')}
                    size='compact'
                />
            </HorizBox>
            <Pad size={24} />
            <SpacesTableRows spaceList={filteredSpaceList} usingSearchOrFilters={usingSearchOrFilters} />
        </View>
    );
}

const SpacesTableStyle = StyleSheet.create({
    table: {
        backgroundColor: colorSurfacePrimary,
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 8,
        padding: 20,
    },
});

export function SpaceFilterWidgetList() {
    const selected = useSessionData('spaceFilter');
    const datastore = useDatastore();
    return (
        <HorizBox center gap={16}>
            <FilterButton
                emoji='🚀'
                label={t('Active')}
                onPress={() => {
                    datastore.setSessionData('spaceFilter', selected === 'active' ? null : 'active');
                }}
                selected={selected === 'active'}
            />
            <FilterButton
                emoji='🔒'
                label={t('Closed')}
                onPress={() => {
                    datastore.setSessionData('spaceFilter', selected === 'closed' ? null : 'closed');
                }}
                selected={selected === 'closed'}
            />
        </HorizBox>
    );
}

export function spaceFilter(datastore: Datastore, space: Space) {
    const selected = datastore.getSessionData('spaceFilter');
    if (selected === 'active') {
        return space.status === 'active' || !space.status;
    } else if (selected === 'closed') {
        return space.status === 'closed';
    } else {
        return true;
    }
}
export const StateFilter: SpaceFilter = {
    key: 'state',
    widget: SpaceFilterWidgetList,
    filter: spaceFilter,
};

export type SpaceProps = Record<string, any>;

export type SpaceTemplate = {
    key: string;
    label: TLabel;
    imageUrl: string;
    structureKey: string;
    description?: TLabel;
    promptLabel?: TLabel;
    promptPlaceholder?: TLabel;
    featuresEnabled: string[];
    featuresDisabled?: string[];
    featuresHidden?: string[];
    nameChecker?: (name: string) => Promise<boolean | string>;
    isCreateAllowed?: ({ spaceProps }: { spaceProps: SpaceProps }) => boolean;
    configWidget?: React.ComponentType<{ spaceProps: SpaceProps; setSpaceProps: (spaceProps: SpaceProps) => void }>;
    editWidget?: React.ComponentType;
};

export type NewScreenWidget = {
    slot: 'after-name';
    component: React.ComponentType<{ values: Record<string, any>; setValues: (values: Record<string, any>) => void }>;
    onCreate?: (datastore: Datastore, questionKey: string, values: Record<string, any>) => void;
};

export type SpacesConfig = {
    filters: SpaceFilter[];
    columns: SpaceColumn[];
    templates: SpaceTemplate[];
    newScreenWidgets?: NewScreenWidget[];
};

export const SpacesFeature: Feature<EditorialDashConfig, SpacesConfig> = {
    key: 'spaces',
    name: 'Spaces',
    dev: true,
    subscreens: {
        newSpace: NewSpaceScreen,
    },
    providesConfig: {
        dashboardTabs: [
            {
                key: 'spaces',
                label: t('Spaces'),
                icon: AddComment,
                component: SpacesTable,
            },
        ],
    },
    receivesConfigDefaults: {
        filters: [StateFilter],
        columns: [FormatColumn, CreatedColumn, ResponsesColumn, StatusColumn, ActionsColumn],
        templates: [],
    },
};
